import React, { ReactNode, useCallback, useContext, useEffect, useMemo } from "react";
import { Alert } from "../components/Alert";
import { CompositeScreenProps, useNavigation } from "@react-navigation/native";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../navigations/RootNavigation";
import { atom, useRecoilState } from "recoil";
import { newMissionState } from "../atoms/core";
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_MISSION, GET_CURRENT_LEGAL_EMPLOYER, UPDATE_MISSION } from "../graphql/core";
import {
    ClientInfo,
    ClientProfile,
    EarningType,
    Mission,
    WorkerInfo,
} from "../__generated__/graphql";
import { createMissionInputFromMission, DEFAULT_HOURS_PER_WEEK, MissionWithIjm } from "../utils";
import { CreateMissionStackParamList, MissionScreens } from "../navigations/CreateMissionStack";
import { useI18n } from "../hooks/i18n-hooks";
import { AuthManagerContext } from "./AuthManager";
import { FIND_MY_MISSIONS } from "../graphql/home";
import { useTracking } from "../hooks/tracking";
import { Buildable } from "ts-essentials";
import { useCurrentProfile } from "../hooks/use-current-profile";

export const currentClientMissionFeesRate = atom({
    key: "currentClientMissionFeesRate",
    default: "0",
});

type MultiChoiceScreen = {
    screens: MissionScreens[];
    selector: (mission: Mission, profile?: ClientProfile) => MissionScreens | null;
    noHeader?: boolean;
};

type StepInfo = {
    isVisible: boolean;
    noHeader?: boolean;
};

interface CreateMissionScreensContextInterface {
    closeWizard: () => void;
    validateScreen: (screen: MissionScreens) => Promise<void>;
    totalSteps: number;
    currentStepIndex: (currentStepName: MissionScreens) => number;
    firstStepIndex: number;
    stepsInfo: StepInfo[];
}

export const CreateMissionScreensContext =
    React.createContext<CreateMissionScreensContextInterface>(
        {} as CreateMissionScreensContextInterface,
    );

type CreateMissionScreensProps = CompositeScreenProps<
    StackScreenProps<RootStackParamList>,
    StackScreenProps<CreateMissionStackParamList>
>;

export type MissionScreensManagerInitialParams = {
    workerInfo?: WorkerInfo;
};

type MissionScreensManagerProps = {
    children?: ReactNode | undefined;
    onValidate?: (mission: Buildable<Mission>) => void; // for testing purpose
    initialParams?: MissionScreensManagerInitialParams;
    testingInitializedData?: Buildable<MissionWithIjm>; // set it to true to skip the initialization of the mission, usefull when recoil state for newMission is already set (storybook , tests )
};

const createMissionCctFeesScreens: MissionScreens[] = ["CreateMissionHotellerieRestaurantFees"];

const cctFeesScreenMapping: Record<string, MissionScreens> = {
    700001: "CreateMissionHotellerieRestaurantFees",
};

/**
 * screenFlow is the definition of the order of the screens of the Create mission wizard.
 * Just put the name of the screen at the wanted index
 * Or if the screen is conditional put an object with with following keys:
 * - screens: an array of screen name that could be at this position.
 * - selector: a function that take a Mission as parameter and return the screen that must be displayed
 * (screen must be one of the screen defined in screens attribute)
 */
export const screensFlow: (MissionScreens | MultiChoiceScreen)[] = [
    "CreateMissionWorker",
    "CreateMissionWorkingAddress",
    "CreateMissionJobTitle",
    "CreateMissionCCT",
    "CreateMissionEarningType",
    {
        screens: ["CreateMissionWorkingTimeMore"],
        selector: (mission): MissionScreens | null => {
            if (mission.currentContract?.earningType !== EarningType.Package) {
                return "CreateMissionWorkingTimeMore";
            }
            return null;
        },
    },
    "CreateMissionDuration",
    "CreateMissionSalary",
    {
        screens: createMissionCctFeesScreens,
        selector: (mission): MissionScreens | null => {
            if (
                mission.currentContract?.cctContractId &&
                cctFeesScreenMapping[mission.currentContract?.cctContractId]
            ) {
                return cctFeesScreenMapping[mission.currentContract?.cctContractId];
            }
            return null;
        },
    },
    "CreateMissionFees",
    "CreateMissionDeductions",
    "CreateMissionTimeSheet",
    "CreateMissionSummary",
    {
        screens: ["CreateMissionPreSign"],
        selector: () => {
            return "CreateMissionPreSign";
        },
        noHeader: true,
    },
];

export const getInitialMissionScreen = (
    profile: ClientProfile,
): MissionScreens | MultiChoiceScreen => {
    for (const screen of screensFlow) {
        if (typeof screen === "string") {
            return screen;
        } else {
            const nextScreen = screen.selector({} as Mission, profile);
            if (nextScreen) {
                return nextScreen;
            }
        }
    }
    return screensFlow[0];
};

const MissionScreensManager = ({
    children,
    onValidate,
    initialParams = {},
    testingInitializedData,
}: MissionScreensManagerProps) => {
    const i18n = useI18n();
    const { user, refreshUser } = useContext(AuthManagerContext);
    const [isMissionInitialized, setIsMissionInitialized] = React.useState(false);
    const navigation = useNavigation<CreateMissionScreensProps["navigation"]>();
    const currentProfile = useCurrentProfile();
    const [mission, setMission] = useRecoilState(newMissionState);
    const [createMission] = useMutation(CREATE_MISSION, {
        refetchQueries: [FIND_MY_MISSIONS],
    });
    const [updateMission] = useMutation(UPDATE_MISSION, {
        refetchQueries: [FIND_MY_MISSIONS],
    });

    const { data: getCurrentLegalEmployerData } = useQuery(GET_CURRENT_LEGAL_EMPLOYER);
    const { trackEvent } = useTracking();
    const [firstStepIndex, setFirstStepIndex] = React.useState(0);

    const stepsInfo = React.useMemo(() => {
        return screensFlow.map((flowItem) => {
            if (typeof flowItem === "string") {
                return { isVisible: true };
            } else {
                const currentScreen = flowItem.selector(
                    mission as Mission,
                    currentProfile as ClientProfile,
                );
                return { isVisible: !!currentScreen, noHeader: flowItem.noHeader };
            }
        });
    }, [currentProfile, mission]);

    const filteredScreenFlow = useMemo(() => {
        return screensFlow.filter((_, i) => stepsInfo[i].isVisible);
    }, [stepsInfo]);

    const getCurrentStepIndex = useCallback(
        (currentStepName: MissionScreens) => {
            return filteredScreenFlow.findIndex((flowItem) => {
                if (flowItem === currentStepName) {
                    return true;
                } else if (typeof flowItem !== "string") {
                    return flowItem.screens.includes(currentStepName);
                }
                return false;
            });
        },
        [filteredScreenFlow],
    );

    useEffect(() => {
        if (getCurrentLegalEmployerData?.getCurrentLegalEmployer) {
            setMission((prevMission) => ({
                ...prevMission,
                legalEmployer: getCurrentLegalEmployerData.getCurrentLegalEmployer,
            }));
        }
    }, [setMission, getCurrentLegalEmployerData]);

    useEffect(() => {
        // effect is rerun in case of hot reload. In this case effect won't be run again
        if (!isMissionInitialized) {
            const clientInfo = user?.clientInfos?.find(
                (clientInfo) => clientInfo.id === currentProfile?.id, // attention ClientInfo id is in fact client profile id.
            ) as ClientInfo;

            if (!clientInfo) {
                return;
            }
            setMission({
                workerInfo: initialParams?.workerInfo,
                currentContract: {
                    cctDetails: { tempdataCallValues: [] },
                    useClientWorkingAddress: false,
                    hoursPerWeekFullTime: clientInfo.hoursPerWeekFullTime || DEFAULT_HOURS_PER_WEEK,
                    vacationDays: 25,
                },
                clientInfo: clientInfo,
                // fill ijm info even if we don't edit / set the values
                ijmSelection: {
                    ijmGroupId: currentProfile?.client?.ijmGroup?.id,
                    ijmGroupName: currentProfile?.client?.ijmGroup?.name,
                    ijmPayFirstDays: currentProfile?.client?.ijmPayFirstDays,
                    ijmPayRemainingPercents: currentProfile?.client?.ijmPayRemainingPercents,
                },
                legalEmployer: getCurrentLegalEmployerData?.getCurrentLegalEmployer,
                ...(testingInitializedData || {}),
            });
            setIsMissionInitialized(true);
            const firstStepName = nextScreenName(-1);
            setFirstStepIndex(getCurrentStepIndex(firstStepName));
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const closeWizard = React.useCallback(() => {
        if (!mission.workerInfo) {
            // in case user did not start editing the mission we do not ask for confirmation
            navigation.popToTop();
        } else {
            Alert.alert(
                i18n.t("createMissionManager.close_alert_title"),
                i18n.t("createMissionManager.close_alert_description"),
                [
                    {
                        text: i18n.t("createMissionManager.close_alert_confirm"),
                        onPress: () => {
                            void trackEvent("click_close_create_mission");
                            navigation.popToTop();
                        },
                        style: "destructive",
                    },
                    {
                        text: i18n.t("createMissionManager.close_alert_cancel"),
                        style: "cancel",
                    },
                ],
                { cancelable: true },
            );
        }
    }, [mission.workerInfo, navigation, i18n, trackEvent]);

    const nextScreenName = useCallback(
        (currentIndex: number): MissionScreens => {
            const flowItem = filteredScreenFlow[currentIndex + 1];
            if (typeof flowItem === "string") {
                return flowItem;
            } else {
                const nextScreen = flowItem.selector(
                    mission as Mission,
                    currentProfile as ClientProfile,
                );
                if (nextScreen) {
                    return nextScreen;
                }
                return nextScreenName(currentIndex + 1);
            }
        },
        [currentProfile, filteredScreenFlow, mission],
    );

    const validateScreen = React.useCallback(
        async (screen: MissionScreens) => {
            if (onValidate) {
                return onValidate(mission);
            }
            const currentScreenIndex = getCurrentStepIndex(screen);
            let contractId = mission.currentContract?.id;
            // we want to create the mission at the summary step. Onc e mission is created we will
            // then update the mission
            if (screen === "CreateMissionSummary") {
                // we need to create the mission
                const variables = {
                    missionData: createMissionInputFromMission(mission as MissionWithIjm),
                    generateContractAsset: true,
                };
                void trackEvent("click_send_mission");
                if (!mission.id) {
                    const result = await createMission({
                        variables,
                    });
                    if (!result.errors && result.data?.createMission) {
                        contractId = result.data?.createMission.currentContract?.id;
                        setMission(result.data?.createMission);
                    } else {
                        throw new Error(i18n.t("createMissionManager.create_mission_failed"));
                    }
                } else {
                    const result = await updateMission({
                        variables: { id: mission.id, ...variables },
                    });
                    if (!result.errors && result.data?.updateMission) {
                        setMission(result.data?.updateMission);
                        contractId = result.data?.updateMission.currentContract?.id;
                    } else {
                        throw new Error(i18n.t("createMissionManager.update_mission_failed"));
                    }
                }
                void trackEvent("mission_created");
                await refreshUser?.();
            }

            if (currentScreenIndex < filteredScreenFlow.length) {
                const nextScreen = nextScreenName(currentScreenIndex);

                if (nextScreen === "CreateMissionPreSign") {
                    if (contractId) {
                        navigation.navigate("CreateMissionPreSign", { contractId });
                    } else {
                        throw new Error(i18n.t("createMissionManager.create_mission_failed"));
                    }
                } else {
                    navigation.navigate(nextScreen);
                }
            } else {
                // if it is the last step we can display the Done message
                navigation.popToTop();
            }
        },
        [
            onValidate,
            getCurrentStepIndex,
            mission,
            filteredScreenFlow.length,
            trackEvent,
            refreshUser,
            createMission,
            setMission,
            i18n,
            updateMission,
            nextScreenName,
            navigation,
        ],
    );

    return (
        <CreateMissionScreensContext.Provider
            value={{
                closeWizard,
                validateScreen,
                totalSteps: filteredScreenFlow.length - 1,
                currentStepIndex: getCurrentStepIndex,
                stepsInfo,
                firstStepIndex,
            }}
        >
            {children}
        </CreateMissionScreensContext.Provider>
    );
};

export default MissionScreensManager;
